import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbTypeaheadModule, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { APP_CONFIG, convertToLocalDate, Country, dateFormat, DateOnly, PracticeType, RoleInformationType, RoleTypeEnum, SURGEON_CODE, USA_CODE, USState } from '../core';
import { CoordinateAbbreviated, emailAlreadyExists, IUserRequestControl, SearchPoiResult, setConditionalValidators, userReqErrorMsgs, UserRequestControl, UserRequestForm } from '../models';
import { MapsService, UserRequestService } from '../services';
import { BaseComponent, DatepickerComponent, FormValidationMsgDirective } from '../shared';
import { MapComponent } from './map';


/**
* User request form component.
*/
@Component({
	selector: 'app-user-request',
	templateUrl: './user-request.component.html',
	styleUrls: ['./user-request.component.css'],
	imports: [
		FormsModule,
		MapComponent,
		NgbTypeaheadModule,
		ReactiveFormsModule,
		DatepickerComponent,
		FormValidationMsgDirective
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class UserRequestComponent extends BaseComponent implements OnInit, OnDestroy {

	public readonly config = inject(APP_CONFIG);
	public readonly mapsSrv = inject(MapsService);
	private readonly formBuilder = inject(FormBuilder);
	private readonly service = inject(UserRequestService);

	readonly buildVer: string = this.config.buildVer;
	readonly envName: string = this.config.envName;

	readonly reqForm = this.formBuilder.nonNullable.group<IUserRequestControl>(new UserRequestControl());

	isFormDetailsVisible = signal<boolean>(false);
	isOtherRoleVisible = signal<boolean>(false);
	isPracticeTypeVisible = signal<boolean>(false);
	isNpiVisible = signal<boolean>(false);

	isStateLicense1Visible = signal<boolean>(false);
	isStateLicense2Visible = signal<boolean>(false);
	isStateLicense3Visible = signal<boolean>(false);
	isStateLicense4Visible = signal<boolean>(false);
	isStateLicense5Visible = signal<boolean>(false);

	isDistributorVisible = signal<boolean>(false);
	isHospitalVisible = signal<boolean>(false);
	isCompanyVisible = signal<boolean>(false);
	isMapVisible = signal<boolean>(false);

	isUSStateVisible = signal<boolean>(false);

	readonly USA_CODE: string = USA_CODE;
	readonly HCP: string = RoleTypeEnum.HCP;
	readonly OTHERS: string = RoleTypeEnum.OTHERS;

	allCountries = signal<Country[]>([]);
	allUSStates = signal<Country[]>([]);
	allOtherRoles = signal<RoleInformationType[]>([]);
	allPracticeTypes = signal<PracticeType[]>([]);

	hospitalPosition = signal<CoordinateAbbreviated | undefined>(undefined);

	readonly maxDateOfBirth: Date = new Date(new Date().setFullYear(new Date().getFullYear() - 18));
	readonly usrErrorMsg = userReqErrorMsgs;
	readonly dateFormat = dateFormat();

	private _emailExistSubscription?: Subscription;

	get formValue(): UserRequestForm {
		return this.reqForm.value as UserRequestForm;
	}

	ngOnInit(): void {
		this.service.initDictionaries().subscribe(() => {
			this.allCountries.set(this.service.getAllCountries());
			this.allUSStates.set(this.service.getAllUSStates());
			this.allPracticeTypes.set(this.service.getAllPracticeTypes());
			this.allOtherRoles.set(this.service.getAllRoleTypes().filter(x => x.value != SURGEON_CODE));
		});
		this.initForm();
		this._emailExistSubscription = this.service.emailExistError.pipe(filter(err => !!err)).subscribe(err => this.setEmailExistError());
	}

	/**
	* Init form.
	*/
	initForm(): void {
		setConditionalValidators(this.reqForm);
		const userRequestForm = this.service.getUserRequestForm();
		userRequestForm && this.reqForm.setValue(userRequestForm);
		this.isFormDetailsVisible.set(!!userRequestForm);
		this.initHospitalOrCompany();
		this.initAddress();
		this.initPractice();
	}

	/**
	* Handle hospital/company fields visibility on init.
	*/
	initHospitalOrCompany(): void {
		const isOtherRole = this.formValue.roleType === RoleTypeEnum.OTHERS;
		this.isOtherRoleVisible.set(isOtherRole);
		this.isCompanyVisible.set(isOtherRole);
		this.isHospitalVisible.set(!isOtherRole);
		this.isDistributorVisible.set(!isOtherRole);;
	}

	/**
	* Handle address fields on init.
	*/
	initAddress(): void {
		this.isUSStateVisible.set(this.isUnitedStates());
		if (this.formValue.hospital instanceof Object && this.formValue.hospital.position) {
			this.isMapVisible.set(true);
			this.hospitalPosition.set({ ...this.formValue.hospital.position });
		} else if (this.formValue.company instanceof Object && this.formValue.company.position) {
			this.isMapVisible.set(true);
			this.hospitalPosition.set({ ...this.formValue.company.position });
		}
	}

	/**
	* Handle practice fields on init.
	*/
	initPractice(): void {
		if (this.formValue.practiceType) {
			this.isPracticeTypeVisible.set(true);
			this.isNpiVisible.set(true);
			this.isStateLicense1Visible.set(true);
			this.isStateLicense2Visible.set(!!this.reqForm.value.licenseNumber2);
			this.isStateLicense3Visible.set(!!this.reqForm.value.licenseNumber3);
			this.isStateLicense4Visible.set(!!this.reqForm.value.licenseNumber4);
			this.isStateLicense5Visible.set(!!this.reqForm.value.licenseNumber5);
		}
	}

	/**
	* Update Date of Birth field on date selection from date picker.
	*/
	updateDateOfBirth(date: Date | null): void {
		this.reqForm.controls.dateOfBirth.setValue(date ? new DateOnly(date) : null);
		this.reqForm.controls.dateOfBirthFormat.setValue(convertToLocalDate(date));
		this.reqForm.controls.dateOfBirth.markAsTouched();
	}

	/**
	* Reset Date of Birth field.
	*/
	clearDateOfBirth(): void {
		this.reqForm.controls.dateOfBirth.setValue(null);
		this.reqForm.controls.dateOfBirth.markAsTouched();
		this.reqForm.controls.dateOfBirthFormat.setValue(null);
	}

	/**
	* Check if the countries are the same.
	*/
	compareCountry(c1: Country, c2: Country): boolean {
		return (c1 == null && c2 == null) || (c1?.id === c2?.id)
	}

	/**
	* Display name of selected hospital.
	*/
	hospitalFormatter = (result: string | SearchPoiResult) => typeof (result) === 'string' ? result : result.poi?.name ?? '';

	/**
	* Display name of selected company.
	*/
	companyFormatter = (result: string | SearchPoiResult) => typeof (result) === 'string' ? result : result.poi?.name ?? '';

	/**
	* Update form on Role Type selection.
	*/
	updateRoleType(): void {
		this.isFormDetailsVisible.set(this.formValue.roleType != null);
		if (this.formValue.roleType === RoleTypeEnum.OTHERS) {
			this.isOtherRoleVisible.set(true);
		} else {
			this.isOtherRoleVisible.set(false);
			this.reqForm.controls.otherRole.setValue(null);
			this.reqForm.controls.otherRole.markAsUntouched();
		}
		this.updateHospitalCompanyVisibility();
		this.refreshPractice();
	}

	/**
	* Update hospital/company fields.
	*/
	updateHospitalCompanyVisibility(): void {
		if (this.formValue.roleType === RoleTypeEnum.OTHERS) {
			this.isCompanyVisible.set(true);
			this.isDistributorVisible.set(false);
			this.isHospitalVisible.set(false);
			this.reqForm.controls.distributor.setValue(null);
			this.reqForm.controls.distributor.markAsUntouched();
			this.reqForm.controls.hospital.setValue(null);
			this.reqForm.controls.hospital.markAsUntouched();
			this.selectPOI(null);
		} else {
			this.isCompanyVisible.set(false);
			this.isDistributorVisible.set(true);
			this.isHospitalVisible.set(true);
			this.reqForm.controls.company.setValue(null);
			this.reqForm.controls.company.markAsUntouched();
			this.selectPOI(null);
		}
	}

	/**
	* Update address fields on country selection.
	*/
	updateCountry(): void {
		if (this.isUnitedStates()) {
			this.isUSStateVisible.set(true);
			this.reqForm.controls.stateProvince.setValue(null);
			this.reqForm.controls.stateProvince.markAsUntouched();
			this.reqForm.controls.usStateProvince.setValue(this.reqForm.value.usStateProvince ?? null);
			this.reqForm.controls.usStateProvince.markAsTouched();
		} else {
			this.isUSStateVisible.set(false);
			this.reqForm.controls.usStateProvince.setValue(null);
			this.reqForm.controls.usStateProvince.markAsUntouched();
			this.reqForm.controls.stateProvince.setValue(this.reqForm.value.stateProvince ?? null);
			this.reqForm.value.country ? this.reqForm.controls.stateProvince.markAsTouched() : this.reqForm.controls.stateProvince.markAsUntouched();
		}
		this.refreshPractice();
	}

	/**
	* Reset practice type field.
	*/
	refreshPractice(): void {
		if (this.isUnitedStates() && this.formValue.roleType === this.HCP) {
			this.isPracticeTypeVisible.set(true);
		} else {
			this.isPracticeTypeVisible.set(false);
			this.reqForm.controls.practiceType.setValue(null);
			this.reqForm.controls.practiceType.markAsUntouched();
		}
		this.updatePracticeType();
	}

	/**
	* Reset practice fields.
	*/
	updatePracticeType(): void {
		if (this.formValue.practiceType) {
			this.isNpiVisible.set(true);
			this.isStateLicense1Visible.set(true);
		} else {
			this.reqForm.controls.npi.setValue(null);
			this.reqForm.controls.npi.markAsUntouched();
			this.isNpiVisible.set(false);
			this.reqForm.controls.stateCode1.setValue(null);
			this.reqForm.controls.licenseNumber1.setValue(null);
			this.reqForm.controls.stateCode1.markAsUntouched();
			this.reqForm.controls.licenseNumber1.markAsUntouched();
			this.isStateLicense1Visible.set(false);
			this.removeStateLicense(2);
			this.removeStateLicense(3);
			this.removeStateLicense(4);
			this.removeStateLicense(5);
		}
	}

	/**
	* Remove state license fields.
	*/
	removeStateLicense(num: number): void {
		(this.reqForm.controls as any)[`stateCode${num}`].setValue(null);
		(this.reqForm.controls as any)[`licenseNumber${num}`].setValue(null);
		(this.reqForm.controls as any)[`stateCode${num}`].markAsUntouched();
		(this.reqForm.controls as any)[`licenseNumber${num}`].markAsUntouched();
		(this as any)[`isStateLicense${num}Visible`].set(false);
	}

	/**
	* Update state code field.
	*/
	updateStateCode(num: number): void {
		(this.reqForm.controls as any)[`licenseNumber${num}`].setValue((this.formValue as any)[`licenseNumber${num}`]);
		(this.reqForm.controls as any)[`licenseNumber${num}`].markAsTouched();
	}

	/**
	* Update license number fiels.
	*/
	updateLicenseNumber(num: number): void {
		(this.reqForm.controls as any)[`stateCode${num}`].setValue((this.formValue as any)[`stateCode${num}`]);
		(this.reqForm.controls as any)[`stateCode${num}`].markAsTouched();
	}

	/**
	* Update address fields on hospital selection.
	*/
	updateHospital(): void {
		if (!(this.reqForm.value.hospital instanceof Object)) {
			this.selectPOI(null);
		}
	}

	/**
	* Update address fields on company selection.
	*/
	updateCompany(): void {
		if (!(this.reqForm.value.company instanceof Object)) {
			this.selectPOI(null);
		}
	}

	/**
	* Update address fields on autocomplete result selection.
	*/
	selectPOI(ev: NgbTypeaheadSelectItemEvent | null): void {
		const item = ev?.item as SearchPoiResult;
		if (item?.address) {
			this.reqForm.controls.address.setValue(item.address.streetName && item.address.streetNumber ? item.address.streetName + " " + item.address.streetNumber : null);
			this.reqForm.controls.city.setValue(item.address.municipality ?? null);
			this.reqForm.controls.postalCode.setValue(item.address?.postalCode ?? null);
			this.reqForm.controls.country.setValue(this.getCountryByCode(item.address.countryCode ?? ''));
			this.updateCountry();
			if (this.isUnitedStates()) {
				this.reqForm.controls.usStateProvince.setValue(this.getUSStateByCode(item.address.countrySubdivision ?? ''));
			} else {
				this.reqForm.controls.stateProvince.setValue(item.address.countrySubdivisionName ?? item.address.countrySubdivision ?? null);
			}
			this.isMapVisible.set(true);
			this.hospitalPosition.set({ lat: item.position?.lat, lon: item.position?.lon });
			this.reqForm.controls.address.markAsTouched();
			this.reqForm.controls.city.markAsTouched();
			this.reqForm.controls.postalCode.markAsTouched();
			this.reqForm.controls.country.markAsTouched();
		} else if (this.hospitalPosition != null) {
			this.reqForm.controls.address.setValue(null);
			this.reqForm.controls.city.setValue(null);
			this.reqForm.controls.postalCode.setValue(null);
			this.reqForm.controls.country.setValue(null);
			this.reqForm.controls.stateProvince.setValue(null);
			this.reqForm.controls.usStateProvince.setValue(null);
			this.updateCountry();
			this.isMapVisible.set(false);
			this.hospitalPosition.set(undefined);
			this.reqForm.controls.address.markAsUntouched();
			this.reqForm.controls.city.markAsUntouched();
			this.reqForm.controls.postalCode.markAsUntouched();
			this.reqForm.controls.country.markAsUntouched();
		}
	}

	/**
	* Handle user request on form submitting.
	*/
	onSubmit(): void {
		this.service.handleFormSubmit(this.reqForm.value as UserRequestForm);
	}

	private isUnitedStates(): boolean {
		return this.formValue.country?.code == USA_CODE;
	}

	private getCountryByCode(code: string): Country | null {
		const result: Country[] = this.allCountries().filter(c => c.code == code);
		return result.length == 1 ? result[0] : null;
	}

	private getUSStateByCode(code: string): USState | null {
		const result: USState[] = this.allUSStates().filter(c => c.code == code);
		return result.length == 1 ? result[0] : null;
	}

	private setEmailExistError(): void {
		this.reqForm.controls.email.setErrors({ [`${emailAlreadyExists}`]: null });
		document.getElementById('email')?.scrollIntoView({ behavior: 'smooth' });
	}


	ngOnDestroy(): void {
		this._emailExistSubscription?.unsubscribe();
	}

}
